import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ClanList } from "./clan-list";
import { ChatPane } from "./chat-pane";
import { cx } from "@/components/ui";
import { Avatar } from "@/components/avatar";
import {
  listNotifications,
  markNotificationsRead,
  notificationsUnreadCount,
} from "@/api/notifications";

type Tab = "classes" | "messages";

function timeAgo(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString();
}

/** Bell + dropdown activity center. Reads /notifications; opening clears unread. */
function NotificationsBell() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const countQuery = useQuery({
    queryKey: ["notifications-count"],
    queryFn: notificationsUnreadCount,
    refetchInterval: 60_000,
  });
  const listQuery = useQuery({
    queryKey: ["notifications"],
    queryFn: listNotifications,
    enabled: open,
  });
  const readMut = useMutation({
    mutationFn: markNotificationsRead,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications-count"] }),
  });
  const unread = countQuery.data?.count ?? 0;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => {
          const next = !open;
          setOpen(next);
          if (next && unread > 0) readMut.mutate();
        }}
        className="relative rounded-full p-2 text-ink-soft hover:bg-black/5"
        aria-label={t("Activity")}
      >
        <span className="text-xl">🔔</span>
        {unread > 0 && (
          <span className="absolute right-0 top-0 min-w-4 rounded-full bg-red-500 px-1 text-[10px] font-bold leading-4 text-white">
            {Math.min(unread, 99)}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 z-20 mt-2 max-h-96 w-80 overflow-y-auto rounded-xl border border-black/10 bg-white p-2 shadow-xl">
          <p className="px-2 py-1 text-xs font-semibold uppercase tracking-wide text-ink-faint">
            {t("Activity")}
          </p>
          {(listQuery.data?.items ?? []).length === 0 && (
            <p className="px-2 py-6 text-center text-sm text-ink-faint">{t("Nothing yet.")}</p>
          )}
          {listQuery.data?.items.map((n) => (
            <div key={n.id} className="flex items-start gap-2 rounded-lg px-2 py-2 hover:bg-black/5">
              <Avatar url={n.actor_avatar_url} name={n.actor_display_name ?? "?"} size={28} />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-ink">{n.title}</p>
                <p className="truncate text-xs text-ink-faint">{n.body}</p>
              </div>
              <span className="shrink-0 text-[10px] text-ink-faint">{timeAgo(n.created_at)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function CommunityPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>("classes");

  const tabLabel: Record<Tab, string> = {
    classes: t("Classes"),
    messages: t("Messages"),
  };

  return (
    <div className="animate-fade-up">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("Community")}</h1>
          <p className="mt-1 text-ink-faint">{t("Join classes, share sets, and message your peers.")}</p>
        </div>
        <NotificationsBell />
      </div>

      <div className="mt-6 inline-flex rounded-xl bg-black/5 p-1">
        {(["classes", "messages"] as Tab[]).map((tabKey) => (
          <button
            key={tabKey}
            onClick={() => setTab(tabKey)}
            className={cx(
              "rounded-lg px-4 py-1.5 text-sm font-semibold capitalize transition",
              tab === tabKey ? "bg-white text-brand-700 shadow-soft" : "text-ink-faint hover:text-ink",
            )}
          >
            {tabLabel[tabKey]}
          </button>
        ))}
      </div>

      <div className="mt-6">{tab === "classes" ? <ClanList /> : <ChatPane />}</div>
    </div>
  );
}
